import { parseName } from "humanparser";

import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import contributorRoles from "astrid-firestore/src/api/articles/constants/contributorRoles";

import Flex from "../../../../components/Flex/Flex";
import InstantSearch from "../../../ui/components/InstantSearch/InstantSearch";
import InstantSearchAutocomplete from "../../../ui/components/InstantSearch/InstantSearchAutocomplete";
import Label from "../../../ui/components/Label/Label";

import Form from "../../../forms/components/Form/Form";

import useArticleContributorRolesOptions from "../../hooks/useArticleContributorRolesOptions";
import useArticleContributorRolesTranslations from "../../hooks/useArticleContributorRolesTranslations";
import useArticleUnnamedPersonsOptions from "../../hooks/useArticleUnnamedPersonsOptions";

export default function ArticleMetaDataContributors({ roles, ...props }) {
	const { t } = useTranslation();

	const { setValue } = useFormContext();

	const contributorRolesTranslations = useArticleContributorRolesTranslations();
	const contributorRolesOptions = useArticleContributorRolesOptions({ roles });
	const unnamedPersonsOptions = useArticleUnnamedPersonsOptions();

	const roleAttributeMap = {
		[contributorRoles.NARRATOR]: "narrators",
		[contributorRoles.AUTHOR]: "authors",
		[contributorRoles.TRANSLATOR]: "translators",
	};

	const onSelect = (index, value) => {
		const { firstName, middleName, lastName } = parseName(value);

		setValue(`contributors.${index}.firstName`, middleName ? `${firstName} ${middleName}` : firstName);
		setValue(`contributors.${index}.lastName`, lastName);
	};

	return (
		<Form.FormFieldArrayTable
			name="contributors"
			header={t("contributors", "Contributors")}
			onAppend={() => ({
				role: contributorRolesOptions[0]?.value || null,
				edit: true,
			})}
			{...props}
		>
			{({ role, edit, firstName, lastName, unnamedPersonsOnixCode }, index) =>
				!edit ? (
					<Flex justifyContent="flex-start" gap={10}>
						<div>
							{firstName} {lastName}
						</div>
						<Label
							size="tiny"
							onClick={() => setValue(`contributors.${index}.edit`, true)}
							style={{ cursor: "pointer" }}
						>
							{contributorRolesTranslations[role]}
						</Label>
					</Flex>
				) : (
					<Form.Group widths="equal">
						{contributorRolesOptions.length > 1 && (
							<Form.Select
								fluid
								name={`contributors.${index}.role`}
								value={role}
								placeholder={t("role", "Role")}
								options={contributorRolesOptions}
							/>
						)}

						{role === contributorRoles.NARRATOR && (
							<Form.Select
								fluid
								name={`contributors.${index}.unnamedPersonsOnixCode`}
								value={unnamedPersonsOnixCode}
								placeholder={t("human", "Human")}
								options={unnamedPersonsOptions}
								additionalOptions={[
									{
										key: null,
										value: null,
										text: t("human", "Human"),
									},
								]}
							/>
						)}

						<InstantSearch indexName="articles">
							<Form.Field name={`contributors.${index}.firstName`}>
								<InstantSearchAutocomplete
									placeholder={t("firstName", "First name")}
									attribute={roleAttributeMap[role]}
									onSelect={(value) => onSelect(index, value)}
								/>
							</Form.Field>

							<Form.Field name={`contributors.${index}.lastName`}>
								<InstantSearchAutocomplete
									placeholder={t("lastName", "Last name")}
									attribute={roleAttributeMap[role]}
									onSelect={(value) => onSelect(index, value)}
								/>
							</Form.Field>
						</InstantSearch>
					</Form.Group>
				)
			}
		</Form.FormFieldArrayTable>
	);
}
